import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import WidgetWithChart from 'components/kea/widgets/common/WidgetWithChart';
import { analyticsChartTheme } from 'util/chartUtils';
import styles from './HealthKpiRow.module.scss';

const SPARKLINE_TONES = {
  searches: { stroke: analyticsChartTheme.searches, fill: analyticsChartTheme.searchesFill },
  ctr: { stroke: analyticsChartTheme.clickRate, fill: analyticsChartTheme.clickRateFill },
  zero: { stroke: analyticsChartTheme.zeroResults, fill: analyticsChartTheme.zeroResultsFill },
  clicks: { stroke: analyticsChartTheme.clickEvents, fill: analyticsChartTheme.clickEventsFill },
};

export const SparklineTooltip = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload || !payload.length) return null;
  const date = payload[0]?.payload?.label || label;
  return (
    <div className={styles.sparklineTooltip}>
      <p className={styles.sparklineLabel}>{date}</p>
      <p className={styles.sparklineValue}>{valueFormatter(payload[0].value)}</p>
    </div>
  );
};

SparklineTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    payload: PropTypes.shape({ label: PropTypes.string }),
  })),
  label: PropTypes.string,
  valueFormatter: PropTypes.func,
};

SparklineTooltip.defaultProps = {
  active: false,
  payload: [],
  label: '',
  valueFormatter: (value) => value,
};

const Sparkline = ({ data, dataKey, tone, valueFormatter }) => {
  const colors = SPARKLINE_TONES[tone];
  const gradientId = `analytics-spark-${tone}-${dataKey}`;
  return (
    <div className={styles.sparklineFrame}>
      <ResponsiveContainer width="100%" height={88}>
        <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`${gradientId}-fill`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.fill} stopOpacity={1} />
              <stop offset="100%" stopColor={colors.fill} stopOpacity={0.12} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey={dataKey}
            strokeWidth={2.5}
            stroke={colors.stroke}
            fill={`url(#${gradientId}-fill)`}
            fillOpacity={1}
            dot={false}
            isAnimationActive={false}
          />
          <Tooltip
            cursor={false}
            content={<SparklineTooltip valueFormatter={valueFormatter} />}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

Sparkline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  dataKey: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(Object.keys(SPARKLINE_TONES)).isRequired,
  valueFormatter: PropTypes.func,
};

Sparkline.defaultProps = {
  data: [],
  valueFormatter: (value) => Number(value).toLocaleString(),
};

export default function HealthKpiRow({
  topQueries,
  zeroResults,
  localizeMessage,
  columns,
  selectedMetric,
  onSelectMetric,
}) {
  const curveItems = (topQueries.curve && topQueries.curve.data) ? topQueries.curve.data : [];
  const zeroCurve = (zeroResults.curve && zeroResults.curve.data) ? zeroResults.curve.data : [];
  const canRenderSparkline = typeof ResizeObserver !== 'undefined';

  const totalSearches = topQueries.queries ? topQueries.queries.total : 0;
  const ctrRaw = topQueries.queries ? (topQueries.queries.ctr || 0) : 0;
  const ctrDisplay = `${(ctrRaw * 100).toFixed(1)}%`;
  const zeroCount = zeroResults.queries
    ? (zeroResults.queries.filteredTotal ?? zeroResults.queries.total ?? 0)
    : 0;
  const lblErrLoad = localizeMessage('search.health.preview.error');

  const totalClickEvents = useMemo(
    () => curveItems.reduce((acc, d) => acc + (d.click ?? 0), 0),
    [curveItems],
  );

  const isLoadingMain = topQueries.isFetching;
  const isLoadingZero = zeroResults.isFetching;
  const mdColumns = columns === 2 ? 6 : 3;

  return (
    <>
      <Grid
        item
        xs={12}
        sm={6}
        md={mdColumns}
        className={styles.kpiItem}
        data-testid="analytics-total-searches"
      >
        <WidgetWithChart
          title={localizeMessage('search.health.kpi.totalSearches')}
          mainNumber={topQueries.error ? '—' : totalSearches.toLocaleString()}
          description={topQueries.error ? lblErrLoad : localizeMessage('search.health.kpi.totalSearches.desc')}
          isFetching={isLoadingMain}
          tone="searches"
          selected={Boolean(onSelectMetric) && selectedMetric === 'searches'}
          onSelect={onSelectMetric ? () => onSelectMetric('searches') : null}
          chart={topQueries.error || !canRenderSparkline ? null : (
            <Sparkline data={curveItems} dataKey="total" tone="searches" />
          )}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
        md={mdColumns}
        className={styles.kpiItem}
        data-testid="analytics-click-rate"
      >
        <WidgetWithChart
          title={localizeMessage('search.health.kpi.clickRate')}
          mainNumber={topQueries.error ? '—' : ctrDisplay}
          description={topQueries.error ? lblErrLoad : localizeMessage('search.health.kpi.clickRate.desc')}
          isFetching={isLoadingMain}
          tone="clickRate"
          selected={Boolean(onSelectMetric) && selectedMetric === 'clickRate'}
          onSelect={onSelectMetric ? () => onSelectMetric('clickRate') : null}
          chart={topQueries.error || !canRenderSparkline ? null : (
            <Sparkline
              data={curveItems}
              dataKey="ctr"
              tone="ctr"
              valueFormatter={(value) => `${Number(value).toFixed(1)}%`}
            />
          )}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
        md={mdColumns}
        className={styles.kpiItem}
        data-testid="analytics-zero-results"
      >
        <WidgetWithChart
          title={localizeMessage('search.health.kpi.zeroResult')}
          mainNumber={zeroResults.error ? '—' : zeroCount.toLocaleString()}
          description={zeroResults.error ? lblErrLoad : localizeMessage('search.health.kpi.zeroResult.desc')}
          isFetching={isLoadingZero}
          tone="zeroResults"
          selected={Boolean(onSelectMetric) && selectedMetric === 'zeroResults'}
          onSelect={onSelectMetric ? () => onSelectMetric('zeroResults') : null}
          chart={zeroResults.error || !canRenderSparkline ? null : (
            <Sparkline data={zeroCurve} dataKey="pv" tone="zero" />
          )}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
        md={mdColumns}
        className={styles.kpiItem}
        data-testid="analytics-click-events"
      >
        <WidgetWithChart
          title={localizeMessage('search.health.kpi.clickEvents')}
          mainNumber={topQueries.error ? '—' : totalClickEvents.toLocaleString()}
          description={topQueries.error ? lblErrLoad : localizeMessage('search.health.kpi.clickEvents.desc')}
          isFetching={isLoadingMain}
          tone="clickEvents"
          selected={Boolean(onSelectMetric) && selectedMetric === 'clickEvents'}
          onSelect={onSelectMetric ? () => onSelectMetric('clickEvents') : null}
          chart={topQueries.error || !canRenderSparkline ? null : (
            <Sparkline data={curveItems} dataKey="click" tone="clicks" />
          )}
        />
      </Grid>
    </>
  );
}

const reportShape = PropTypes.shape({
  isFetching: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  queries: PropTypes.shape({
    total: PropTypes.number,
    filteredTotal: PropTypes.number,
    ctr: PropTypes.number,
  }),
  curve: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({})),
  }),
});

HealthKpiRow.propTypes = {
  topQueries: reportShape.isRequired,
  zeroResults: reportShape.isRequired,
  localizeMessage: PropTypes.func.isRequired,
  columns: PropTypes.oneOf([2, 4]),
  selectedMetric: PropTypes.oneOf(['searches', 'clickRate', 'zeroResults', 'clickEvents']),
  onSelectMetric: PropTypes.func,
};

HealthKpiRow.defaultProps = {
  columns: 4,
  selectedMetric: 'searches',
  onSelectMetric: null,
};
